<template>
  <div
    :class="_class"
    :draggable="draggable && !dragHandle"
    @dragstart="handleDragStart"
    @drop="handleDrop"
    @dragover="handleDragOver"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
  >
    <span v-if="draggable && dragHandle" class="drag-handle" draggable="true"
      >&#9776;</span
    >
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
const DATA_MIME_TYPE = "application/catalyst-data";
// We use the group prefix to know and filter the drop targets with
const DATA_GROUP_PREFIX = "application/catalyst-group-";
const DROP_TARGET_OUTLINE = "drop-target";
const DROP_TARGET_OUTLINE_CLASS = "drop-target-outline";

/**
 * Drag-n-drop component. This will fire a 'ui-dropped' event when an item is dropped
 * on this component. The event will contain:
 *   event: The dropping event
 *   data: The data that was dropped.
 */
export default defineComponent({
  name: "CatDragdrop",
  props: {
    draggable: {
      // Default to this item being draggable
      type: Boolean,
      default: true
    },
    droppable: {
      // Default item to not being droppable
      type: Boolean,
      default: false
    },
    populateDragData: {
      // Populate the data on the start of a drag. An object is expected to be returned.
      // By default this is just the id and inner text of the drag target
      type: Function,
      default: function (event) {
        return {
          sourceId: this.id,
          text: event.target.innerText
        };
      }
    },
    dropOutline: {
      // Outline the drop target
      type: Boolean,
      default: false
    },
    blockElement: {
      // Define if this is a block element or inline-block.
      type: Boolean,
      default: true
    },
    dragHandle: {
      // Use a drag handle rather than the whole element
      type: Boolean,
      default: false
    },
    dragGroups: {
      // Array of group names to assign to this drag. This controls what drop targets are available.
      type: Array,
      default: function () {
        return ["default"];
      }
    },
    dropGroups: {
      // Array of group names that this drop target will accept drags from.
      type: Array,
      default: function () {
        return ["default"];
      }
    }
  },
  computed: {
    _class: function () {
      return {
        "cat-dragdrop": true,
        "cat-dragdrop-inline": !this.blockElement
      };
    }
  },
  methods: {
    handleDragStart: function (event) {
      this.log("Handle drag start called");
      event.stopPropagation(); // we have to stop propagation for drop targets within drop targets
      event.dataTransfer.setData(
        DATA_MIME_TYPE,
        JSON.stringify(this.populateDragData(event))
      );
      this.dragGroups.forEach(function (grp) {
        event.dataTransfer.setData(DATA_GROUP_PREFIX + grp, "{}");
      });
      this.$emit("ui-drag-start", event);
    },
    handleDrop: function (event) {
      event.preventDefault();
      if (!this.canBeDropped(event)) {
        return;
      }
      this.$el.classList.remove(this.dropClassName());

      const data = this.getData(event);
      // Emit the drop event
      this.$emit("ui-dropped", {
        event: event,
        data: data
      });
      this.log("Handle drop data = " + data);
    },
    handleDragOver: function (event) {
      // Don't log in this function as it is called a lot
      event.preventDefault(); // must!!!
      if (this.canBeDropped(event)) {
        event.dataTransfer.dropEffect = "move";
        if (!this.$el.classList.contains(this.dropClassName())) {
          this.$el.classList.add(this.dropClassName());
        }
      }
    },
    handleDragEnter: function (event) {
      event.preventDefault();
      if (!this.canBeDropped(event)) {
        return;
      }
      this.log("handleDragEnter");
      this.$el.classList.add(this.dropClassName());
    },
    handleDragLeave: function (event) {
      event.preventDefault();
      if (!this.canBeDropped(event)) {
        return;
      }
      this.log("handleDragLeave");
      this.$el.classList.remove(this.dropClassName());
    },
    // Get the data from the event. Returns null if there isn't any
    getData: function (event) {
      return JSON.parse(event.dataTransfer.getData(DATA_MIME_TYPE));
    },
    // This has its own log function so we can switch it out to do a "cl()" log if needed
    log: function (msg) {
      //console.log(msg);
    },
    // Get the drop CSS class name
    dropClassName: function () {
      return this.dropOutline ? DROP_TARGET_OUTLINE_CLASS : DROP_TARGET_OUTLINE;
    },
    // Determine if this drag event can be dropped on this target
    canBeDropped: function (event) {
      if (this.droppable) {
        for (let i = 0; i < this.dropGroups.length; i++) {
          if (
            event.dataTransfer.types.indexOf(
              DATA_GROUP_PREFIX + this.dropGroups[i]
            ) !== -1
          ) {
            return true;
          }
        }
      }
      return false;
    }
  }
});
</script>
